import type { BlockPromptContext } from './types.ts';
import { buildProgressionClause, buildExampleClause, buildCrossMonthClause } from './types.ts';
import { buildPersonalizationClause } from './personalization-helpers.ts';
import { SOURCE_LANGUAGES, TARGET_LANGUAGES } from '../../constants/languages.ts';

export function buildTranslationChallengePrompt(ctx: BlockPromptContext): string {
  const source = SOURCE_LANGUAGES.find((l) => l.code === ctx.profile.native_language);
  const target = TARGET_LANGUAGES.find((l) => l.code === ctx.profile.target_language);

  return `Generate a **Translation Challenge** exercise block for a ${ctx.profile.level.toUpperCase()} ${target?.name} learner whose native language is ${source?.name}.

## Theme
Workbook theme: "${ctx.workbookTheme}"
Sub-theme for this block: "${ctx.subTheme}"

## Block Position
This is block ${ctx.blockOrder} of ${ctx.totalBlocks}.
${buildPersonalizationClause(ctx.profile, ctx.blockOrder, ctx.isPreview)}

## Requirements

1. Create **5 sentences** for \`toTarget\` (${source?.name} → ${target?.name}) and **4 sentences** for \`toNative\` (${target?.name} → ${source?.name}).
2. Each item has a difficulty: "easy", "medium", or "hard". Include a mix — at least 2 different levels.
3. Sentences should relate to "${ctx.subTheme}" and reinforce vocabulary from the workbook.
4. \`source\` is the sentence the learner reads. \`answer\` is the expected translation.
5. "easy" = short, direct translation. "medium" = moderate clause structure. "hard" = idiomatic or complex grammar.
6. **CRITICAL:** \`instructions\` MUST be in ${source?.name} (never in ${target?.name}). \`instructionsNative\` MUST be in ${target?.name}.

## Output Format

Return a single JSON object matching:

\`\`\`typescript
interface TranslationChallengeBlock {
  id: string;
  type: "translation_challenge";
  order: ${ctx.blockOrder};
  title: string;
  titleNative: string;
  theme: "${ctx.subTheme}";
  estimatedPages: 1.5;
  instructions: string;         // ⚠️ MUST be in ${source?.name} — NEVER in ${target?.name}
  instructionsNative: string;  // ⚠️ MUST be in ${target?.name} — brief companion version
  toTarget: Array<{ source: string; answer: string; difficulty: "easy" | "medium" | "hard" }>;
  toNative: Array<{ source: string; answer: string; difficulty: "easy" | "medium" | "hard" }>;
}
\`\`\`

Respond with ONLY the JSON object.${buildProgressionClause(ctx)}${buildExampleClause(ctx, 'translation_challenge')}${buildCrossMonthClause(ctx)}`;
}
